import React, { useEffect, useState } from 'react';
import ApiDatabase from 'server';
import { MissionIcon, PersonIcon, PositiveThumb, NegativeThumb } from '../../../../../assets/icons';
import { dateToEpoch, dateWordCard } from '../../../../../helper/helper';
import MissionInterruptionNotice from 'components/Mission/MissionInterruptionNotice';
import MissionListSection from 'components/Mission/MissionListSection';
import BadgeSquare from 'components/badge/badgeSquare';
import { MISSION_STATUS_BADGE_TYPE } from 'constants/missionStatusBadge';

const SlideOverCompanyMissions = ({ companyInfo }) => {
  const [missions, setMissions] = useState([]);
  const [tempWorkers, setTempWorkers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeView, setActiveView] = useState('missions'); // 'missions' | 'tempWorkers'
  const [groupedMissions, setGroupedMissions] = useState({
    past: [],
    current: [],
    future: [],
  });
  const token = localStorage.getItem('xsrfToken');

  useEffect(() => {
    if (token && companyInfo?._id) {
      setIsLoading(true);
      ApiDatabase.getCompanyMissionsAndTempWorkers(
        { token, idCompany: companyInfo._id },
        (data) => {
          setMissions(data.missions || []);
          setTempWorkers(data.tempWorkers || []);
          setIsLoading(false);
        },
        (err) => {
          console.warn(err);
          setIsLoading(false);
        }
      );
    }
  }, [companyInfo?._id, token]);

  useEffect(() => {
    const pastMissions = [];
    const currentMissions = [];
    const futureMissions = [];
    const now = dateToEpoch(new Date());

    for (let mission of missions) {
      if (!mission.duration?.datestart || !mission.duration?.dateend) continue;
      const dateStart = dateToEpoch(new Date(mission.duration.datestart));
      const dateEnd = dateToEpoch(new Date(mission.duration.dateend));
      if (dateEnd.getTime() < now.getTime()) {
        pastMissions.push(mission);
      } else if (
        dateStart.getTime() <= now.getTime() &&
        dateEnd.getTime() >= now.getTime()
      ) {
        currentMissions.push(mission);
      } else {
        futureMissions.push(mission);
      }
    }
    setGroupedMissions({
      past: pastMissions,
      current: currentMissions,
      future: futureMissions,
    });
  }, [missions]);

  const filters = [
    { 
      id: 'missions', 
      label: 'Missions', 
      count: missions.length,
      icon: <MissionIcon wh={14} color={activeView === 'missions' ? '#FF9600' : '#6B7280'} />,
    },
    { 
      id: 'tempWorkers', 
      label: 'Intérimaires', 
      count: tempWorkers.length,
      icon: <PersonIcon wh={14} color={activeView === 'tempWorkers' ? '#FF9600' : '#6B7280'} />,
    },
  ];

  const renderMissionItem = (mission, index) => {
    const isInterrupted = mission.status === 'interrupted';

    return (
      <div
        key={index}
        data-mission-card
        className={'bg-white py-3'}
      >
        <div className={'flex justify-between gap-3'}>
          <div className={'flex gap-3 items-start flex-1 min-w-0'}>
            <div className={'flex flex-col gap-1 flex-1 min-w-0'}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-gray-900">{mission.title}</span>
                <span className="text-gray-400">n°{mission.id_mission}</span>
              </div>
              {mission.tempWorker?.firstname && (
                <div className={'text-gray-500 text-sm flex items-center gap-1'}>
                  <PersonIcon wh={12} color="#6B7280" />
                  <span>{mission.tempWorker.firstname} {mission.tempWorker.lastname}</span>
                </div>
              )}
              {!mission.tempWorker?.firstname && (
                <div className={'text-gray-400 text-xs'}>
                  Aucun intérimaire attribué
                </div>
              )}
            </div>
          </div>
          <div className={'flex flex-col items-end gap-1 flex-shrink-0'}>
            {isInterrupted && (
              <BadgeSquare
                type={MISSION_STATUS_BADGE_TYPE.interrupted}
                size="12"
              />
            )}
            {mission.duration?.datestart && mission.duration?.dateend && (
              <div className={'text-gray-400 text-xs text-right whitespace-nowrap'}>
                {dateWordCard(mission.duration.datestart)} &gt; {dateWordCard(mission.duration.dateend)}
              </div>
            )}
          </div>
        </div>
        {isInterrupted && (
          <MissionInterruptionNotice interruption={mission.interruption} />
        )}
      </div>
    );
  };

  const renderTempWorkerItem = (tempWorker, index) => {
    return (
      <div key={index} className={'bg-white border border-gray-200 rounded-lg p-4'}>
        <div className={'flex justify-between gap-3'}>
          <div className={'flex gap-3 items-center flex-1 min-w-0'}>
            <div className={'bg-gray-100 w-10 h-10 flex justify-center items-center rounded-full flex-shrink-0'}>
              <PersonIcon wh={20} color="#6B7280" />
            </div>
            <div className={'flex flex-col gap-1 flex-1 min-w-0'}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-gray-900">
                  {tempWorker.firstname} {tempWorker.lastname}
                </span>
              </div>
              <div className={'text-gray-500 text-sm'}>
                {tempWorker.missionsCount} mission{tempWorker.missionsCount > 1 ? 's' : ''} effectuée{tempWorker.missionsCount > 1 ? 's' : ''}
              </div>
            </div>
          </div>
          <div className={'flex items-center gap-3 flex-shrink-0'}>
            {(tempWorker.ratings?.positive > 0 || tempWorker.ratings?.negative > 0) && (
              <div className="flex items-center gap-2">
                {tempWorker.ratings?.positive > 0 && (
                  <div className="flex items-center gap-1 bg-green-50 text-green-600 px-2 py-1 rounded-full text-xs font-medium">
                    <PositiveThumb wh={12} color="#059669" />
                    <span>{tempWorker.ratings.positive}</span>
                  </div>
                )}
                {tempWorker.ratings?.negative > 0 && (
                  <div className="flex items-center gap-1 bg-red-50 text-red-600 px-2 py-1 rounded-full text-xs font-medium">
                    <NegativeThumb wh={12} color="#DC2626" />
                    <span>{tempWorker.ratings.negative}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={'flex flex-col gap-4 animate-pulse'}>
        <div className="flex gap-2">
          <div className="w-24 h-8 bg-gray-200 rounded"></div>
          <div className="w-24 h-8 bg-gray-200 rounded"></div>
        </div>
        <div className="w-full h-20 bg-gray-200 rounded-lg"></div>
        <div className="w-full h-20 bg-gray-200 rounded-lg"></div>
        <div className="w-full h-20 bg-gray-200 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className={'flex flex-col flex-1 min-h-0'}>
      {/* Filtres */}
      <div
        data-filter-bar
        className={'sticky top-0 z-20 bg-white flex items-end gap-2 pb-3 w-full text-sm'}
      >
        {filters.map((filter) => (
          <div
            key={filter.id}
            onClick={() => setActiveView(filter.id)}
            className={`cursor-pointer pt-1 pb-2 px-2 border-b flex items-center gap-1.5 ${
              activeView === filter.id
                ? 'border-yellow-500 text-yellow-500'
                : 'hover:border-yellow-500 hover:text-yellow-500 text-gray-500'
            }`}
          >
            {filter.icon}
            <span>{filter.label}</span>
            <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
              activeView === filter.id ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-200 text-gray-600'
            }`}>
              {filter.count}
            </span>
          </div>
        ))}
      </div>

      {/* Contenu selon la vue */}
      <div
        data-mission-scroller
        className={'flex flex-col gap-3 text-sm flex-1 min-h-0 overflow-y-auto no-scrollbar'}
      >
        {activeView === 'missions' && (
          <>
            {missions.length > 0 ? (
              <>
                <MissionListSection
                  label="En cours"
                  count={groupedMissions.current.length}
                  tone="current"
                >
                  {groupedMissions.current.map((mission, index) =>
                    renderMissionItem(mission, `current-${index}`)
                  )}
                </MissionListSection>
                <MissionListSection
                  label="À venir"
                  count={groupedMissions.future.length}
                  tone="future"
                >
                  {groupedMissions.future.map((mission, index) =>
                    renderMissionItem(mission, `future-${index}`)
                  )}
                </MissionListSection>
                <MissionListSection
                  label="Passées"
                  count={groupedMissions.past.length}
                  tone="past"
                >
                  {groupedMissions.past.map((mission, index) =>
                    renderMissionItem(mission, `past-${index}`)
                  )}
                </MissionListSection>
              </>
            ) : (
              <div className={'text-gray-500 text-center py-4'}>
                Aucune mission pour cette entreprise
              </div>
            )}
          </>
        )}

        {activeView === 'tempWorkers' && (
          <>
            {tempWorkers.length > 0 ? (
              tempWorkers.map((tempWorker, index) => {
                return renderTempWorkerItem(tempWorker, index);
              })
            ) : (
              <div className={'text-gray-500 text-center py-4'}>
                Aucun intérimaire attribué
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SlideOverCompanyMissions;
